import { useToast } from '@app/core/notifications';
import { pushTo } from '@app/core/routing';
import { socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { useSocialExportFactory } from '@app/modules/social-export/hooks/useSocialExportFactory';
import { socialExport } from '@mi-front/api-clients';
import { useQueryClient } from '@tanstack/react-query';
import { QueryView } from '@ui/utils/async-view/QueryView';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { BasePlatformSettingsPage } from './BasePlatformSettingsPage';
import { ConnectionSuccessAlert } from './ConnectionSuccessAlert';

export function PlatformSettingsPage() {
  const { platform: platformKey, station: stationId } = useParams<{
    platform: string;
    station: string;
  }>();
  const platform = socialPlatforms.get(platformKey)!;
  const { $t } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const queryClient = useQueryClient();
  const { SettingsForm } = useSocialExportFactory(platform)();

  const settingsQuery =
    socialExport.useSettingsGetConnection<socialExport.SettingsGetConnection200>(
      stationId,
      platform.id,
    );

  const mutation = socialExport.useSettingsUpdateConnection({
    mutation: {
      onSuccess: async () => {
        await queryClient.resetQueries({
          queryKey: socialExport.settingsGetConnectionQueryKey(stationId, platform.id),
        });

        pushTo(`/admin/social-export/${platform.id.toLowerCase()}`);

        notifySuccess(
          $t({
            id: 'socialExport.settings.updated',
            defaultMessage: 'Configuración actualizada correctamente',
          }),
        );
      },
    },
  });

  const handleUpdate = async (data: socialExport.SettingsUpdateConnectionMutationRequest) => {
    try {
      await mutation.mutateAsync({
        station: stationId,
        platform: platform.id,
        data,
      });
    } catch (error: unknown) {
      notifyError((error as Error).message);
    }
  };

  return (
    <BasePlatformSettingsPage platform={platform} stationId={stationId}>
      <ConnectionSuccessAlert platform={platform} />
      <QueryView inline query={settingsQuery}>
        {(defaultSettings) => (
          <SettingsForm
            stationId={stationId}
            onUpdate={handleUpdate}
            defaultSettings={defaultSettings}
          />
        )}
      </QueryView>
    </BasePlatformSettingsPage>
  );
}
